import React, { useEffect, useState } from "react";
import { getLists, getList, deleteList, leadsToCSV, downloadCSV } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Bookmark, Download, Trash2, ChevronLeft, Users, Send } from "lucide-react";
import ResultsTable from "./ResultsTable";
import PushModal from "./PushModal";

export default function SavedLists({ settings, instantlyConfigured }) {
  const { toast } = useToast();
  const [lists, setLists] = useState([]);
  const [active, setActive] = useState(null);
  const [selected, setSelected] = useState({});
  const [pushOpen, setPushOpen] = useState(false);

  const load = () => getLists().then(setLists).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    const data = await getList(id);
    setActive(data);
    setSelected({});
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    await deleteList(id);
    toast({ title: "List deleted" });
    load();
    if (active?.id === id) setActive(null);
  };

  const exportList = (list) => {
    downloadCSV(`${list.name}.csv`.replace(/\s+/g, "-").toLowerCase(), leadsToCSV(list.leads, settings));
    toast({ title: "Exported", description: `${list.leads.length} leads downloaded.` });
  };

  if (active) {
    return (
      <main className="max-w-6xl mx-auto px-6 py-8">
        <button onClick={() => setActive(null)} className="inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground mb-4">
          <ChevronLeft size={15} /> Back to lists
        </button>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-display text-[22px] font-bold text-foreground">{active.name}</h1>
            <p className="text-[13px] text-muted-foreground">{active.leads.length} leads</p>
          </div>
          <div className="flex items-center gap-2">
            {instantlyConfigured && (
              <button onClick={() => setPushOpen(true)} className="inline-flex items-center gap-1.5 text-[13px] font-medium border border-brand-200 text-brand-700 bg-brand-50 rounded-lg px-3.5 py-2 hover:bg-brand-100">
                <Send size={15} /> Push to Instantly
              </button>
            )}
            <button onClick={() => exportList(active)} className="inline-flex items-center gap-1.5 text-[13px] font-medium bg-brand-600 text-white rounded-lg px-3.5 py-2 hover:bg-brand-700">
              <Download size={15} /> Export CSV
            </button>
          </div>
        </div>
        <ResultsTable results={active.leads} selected={selected}
          toggle={(id) => setSelected((s) => ({ ...s, [id]: !s[id] }))}
          toggleAll={() => setSelected(active.leads.every((r) => selected[r.id]) ? {} : Object.fromEntries(active.leads.map((r) => [r.id, true])))} />
        <PushModal open={pushOpen} onClose={() => setPushOpen(false)} leads={active.leads} />
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-6 py-8">
      <h1 className="font-display text-[26px] font-bold text-foreground mb-1">Saved lists</h1>
      <p className="text-[14px] text-muted-foreground mb-6">Your saved lead lists, ready to export anytime.</p>

      {lists.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-12 text-center">
          <Bookmark size={28} className="mx-auto text-muted-foreground/40" />
          <p className="mt-3 text-[14px] text-muted-foreground">No saved lists yet. Run a search and save your first list.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map((l) => (
            <div key={l.id} onClick={() => open(l.id)} role="button" tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open(l.id)}
              className="cursor-pointer text-left bg-white rounded-2xl border border-border p-5 hover:shadow-md hover:border-brand-300 transition-all group">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center"><Users size={18} className="text-brand-600" /></div>
                <button onClick={(e) => remove(l.id, e)} className="text-muted-foreground/50 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
              </div>
              <div className="mt-3 font-medium text-foreground text-[15px] truncate">{l.name}</div>
              <div className="text-[13px] text-muted-foreground">{l.count} leads</div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
